









































import Enumerable from "linq";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { IDrawableItemEditable } from "../../IDrawableItemEditable";
import { IBehaviorDocumentEditorContentsAiPhraseConfiguration } from "../../../../../domain/configuration";
import { FixedPhraseReference, FixedPhraseTextKinds } from "../../../../../domain/document";
import { DialogSelector, FixedLoading, instanceOfITreeExpandable } from "../../../../system";
import CacheableComponent from "../../../../system/CacheableComponent.vue";
import TabSelector from "../../../../system/selectors/TabSelector.vue";
import { FixedPhraseTextDropManager, IFixedPhraseTextDragParameter } from "../../../../dragging";
import {
  instanceOfIEditFixedPhrase,
  instanceOfISelectedFixedPhraseGettable,
} from "../../../fixedPhrase";
import {
  AiUserFixedPhraseSelector,
  AiCommonDepartmentFixedPhraseSelector,
  AiDepartmentFixedPhraseSelector,
  AiFormatFixedPhraseSelector,
} from "../../../aiPhrase";
import { CommonComponentsAccessibleMixin } from "../../mixins";
import { IFreeInputableObject, instanceOfIFreeInputableObject } from "../../objects/IFreeInputableObject";

interface IFixedPhraseToolTemplateTab {
  key: string;
  title: string;
  description: string;
  componentName: string;
}

@Component({
  components: {
    DialogSelector,
    TabSelector,
    CacheableComponent,
    userSelector: AiUserFixedPhraseSelector,
    commonDepartmentSelector: AiCommonDepartmentFixedPhraseSelector,
    departmentSelector: AiDepartmentFixedPhraseSelector,
    formatSelector: AiFormatFixedPhraseSelector,
    FixedLoading,
  }
})
export default class AiPhraseTool extends Mixins(CommonComponentsAccessibleMixin) {
  @Prop()
  source: IDrawableItemEditable;

  @Prop(String)
  userId: string;

  @Prop(String)
  departmentId: string;

  @Prop(Number)
  formatOriginalId: number;

  @Prop({ default: true })
  isPastable: boolean;

  data(): any {
    return {
      selectedTemplateTab: null as IFixedPhraseToolTemplateTab,
      selectedFixedPhrase: null as FixedPhraseReference,
      fixedPhraseTextKind: FixedPhraseTextKinds.Text,
      isFixedPhraseEditing: false,
    };
  }
  selectedTemplateTab: IFixedPhraseToolTemplateTab;
  selectedFixedPhrase: FixedPhraseReference;
  fixedPhraseTextKind: FixedPhraseTextKinds;
  isFixedPhraseEditing: boolean;

  mounted(): void {
    this.setSelectedTemplateTab();
  }

  private get configuration(): IBehaviorDocumentEditorContentsAiPhraseConfiguration {
    return this.$configuration.behavior.document.editor.contents.aiPhrase;
  }

  private get templateTabs(): IFixedPhraseToolTemplateTab[] {
    return Enumerable.from([
        { key: "user",             title: "個人", description: "個人の生成AI定型文を表示します。", componentName: "aiUserFixedPhraseSelector" },
        this.configuration.commonDepartment.isEnable
          ? { key: "commonDepartment", title: "全" + this.$wording.common.departmentAsShort, description: "全" + this.$wording.common.departmentAsShort + "の生成AI定型文を表示します。", componentName: "aiCommonDepartmentFixedPhraseSelector" }
          : null,
        { key: "department",       title: "主" + this.$wording.common.departmentAsShort, description: "主" + this.$wording.common.departmentAsShort + "の生成AI定型文を表示します。", componentName: "aiDepartmentFixedPhraseSelector" },
        { key: "format",           title: "書類", description: "書類の生成AI定型文を表示します。", componentName: "aiFormatFixedPhraseSelector" },
      ])
      .where(t => !!t)
      .toArray();
  }

  setSelectedTemplateTab(): void {
    const key = this.configuration.defaultSelectedTab;
    const defaultSelectedTab = Enumerable.from(this.templateTabs).firstOrDefault(t => t.key === key);
    this.selectedTemplateTab = defaultSelectedTab ? defaultSelectedTab : this.templateTabs[0];
  }


  shouldShowTemplateTabContent(key: string): boolean {
    return this.selectedTemplateTab && (this.selectedTemplateTab.key === key);
  }

  get isAdministratorDepartment(): boolean {
    return this.departmentId && Enumerable.from(this.$store.getters.userAuthority.administratorDepartmentIds as string[])
      .any(d => d === this.departmentId);
  }

  get isCommonItemEditable(): boolean {
    return this.$store.getters.userAuthority.commonItemEditable;
  }

  get isFixedPhraseEditable(): boolean {
    return this.shouldShowTemplateTabContent("user") ||
      (this.shouldShowTemplateTabContent("department") && this.isAdministratorDepartment) ||
      (this.shouldShowTemplateTabContent("commonDepartment") && this.isCommonItemEditable) ||
      (this.shouldShowTemplateTabContent("format") && this.$store.getters.userAuthority.formatEditable);
  }

  get isExpandButtonVisible(): boolean {
    return !this.$configuration.behavior.fixedPhrase.shouldExpandTreeAutomatically;
  }
  
  private isSourcePastable(source: any): source is IFreeInputableObject {
    return instanceOfIFreeInputableObject(source) && source.isFreeInputable && source.shouldInputFreeSelfOrAll;
  }

  async pasteFixedPhrase(): Promise<void> {
    if (!this.isSourcePastable(this.source)) { return; }
    const result = await this.commonComponents.dialogs.aiPhrase.show({
      template: this.selectedFixedPhrase.text,
      actions: {
        insertToCaretPosition: true,
      },
    });
    if (result.isConfirmed) {
      if (result.action === "insertToCaretPosition") {
        this.source.pasteText(result.phrase);
      }
    }
  }

  onDragStart(event: DragEvent, text: string): void {
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData("text", text);
    const data: IFixedPhraseTextDragParameter = {
      kind: this.fixedPhraseTextKind,
      text,
      isAiPhrase: true,
    };
    const manager = new FixedPhraseTextDropManager();
    manager.parameter = data;
  }

  expandTree(): void {
    const tree = this.$refs[this.selectedTemplateTab.componentName];
    if (instanceOfITreeExpandable(tree)) {
      tree.expandTree();
    }
  }

  editFixedPhrase(): void {
    const tree = this.$refs[this.selectedTemplateTab.componentName];
    if (instanceOfIEditFixedPhrase(tree) && !this.isFixedPhraseEditing) {
      this.isFixedPhraseEditing = true;
      tree.editFixedPhrase()
      .finally(() => {
        this.isFixedPhraseEditing = false;
      });
    }
  }

  @Watch("selectedTemplateTab")
  onSelectedTemplateTabUpdated(newValue: IFixedPhraseToolTemplateTab, oldValue: IFixedPhraseToolTemplateTab): void {
    if (!newValue || !oldValue) { return; }
    // 各タブ内で定型文を選択した状態のままタブを切り替えたとき、
    // FixedPhraseToolのselectedFixedPhraseと表示中のタブで選択されている定型文とが一致しなくなってしまうので強制的に整合させている
    const tree = this.$refs[this.selectedTemplateTab.componentName];
    this.selectedFixedPhrase = instanceOfISelectedFixedPhraseGettable(tree)
      ? tree.getSelectedFixedPhrase()
      : null;
  }
}
